import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import type en from '@locales/en';

// Sprint 29 (Addendum 10/10a design in docs/architecture.md, "The stronger fix"). Ported from
// mobile's already-shipped, already-reviewed generic signature
// (`redesign/src/i18n/useTranslation.ts`, sprint 27/story-139) so that reading a catalog key that
// doesn't exist on a given section becomes a compile error instead of `any`. This is what would
// have caught story-150's P0 (three modals reading `tTaskDetail.equipmentModal` etc., which does
// not exist on `taskDetail`) at `tsc` time rather than at render time.
type LocaleBundle = typeof en;
type SectionKey = keyof LocaleBundle;

export function useTranslation<K extends SectionKey>(key: K): LocaleBundle[K] {
  // Sprint 28 change request, corrective pass (story-142-review.md, Critical #1). Bare
  // `useAppSelector` throws immediately if rendered outside a `<Provider>` — that is intentional.
  // A component reachable without the `locale` slice is a genuine bug (a component escaping the
  // Provider tree, a missing reducer, etc.) and must fail loudly, not render a silent blank where
  // a translated label belongs. Under-mocked tests are fixed at the test layer (a real Redux
  // `<Provider>` carrying the `locale` reducer, or an extended `@redux/hooks` mock), never by
  // weakening this contract. Sprint 29: only the compile-time signature above changed — this
  // runtime body, including the bare `useAppSelector` call and its throw-outside-Provider
  // behaviour, is byte-for-byte unchanged. The no-argument overload was dropped (a repo-wide grep
  // found zero real call sites invoking `useTranslation()` with no argument — the only match was
  // inside a code comment), per the architecture doc's measured finding.
  const locale: { [key: string]: any } = useAppSelector(
    (state: RootState) => state.locale.locale,
  );

  return locale[key];
}
